// ============================================================
// dtbase v2：scene_tree（引用式构件树）Schema（规范 §4）
// ============================================================
import { z } from 'zod';
import { VISUAL_TYPES } from '../constants';

const vec3Schema = z.array(z.number()).length(3);

export interface SceneTreeBinding {
  modelNodeId?: string;
  deviceId?: string;
  pointKey?: string;
  dataSetId?: string;
  visualType: (typeof VISUAL_TYPES)[number];
  ruleConfig: Record<string, unknown>;
}

export interface SceneTreeNode {
  id: string;
  name: string;
  collapsed?: boolean;
  pickable?: boolean;
  prefabRef?: { prefabId: string; version?: string };
  transform: { position: number[]; rotation: number[]; scale: number[] };
  visible: boolean;
  locked: boolean;
  layer?: string;
  bindings?: SceneTreeBinding[];
  children?: SceneTreeNode[];
}

export interface SceneTree {
  schemaVersion: '2.0';
  root: SceneTreeNode;
  cameras: Array<{ id: string; name: string; position: number[]; target: number[] }>;
  render: Record<string, unknown>;
}

const bindingSchema = z
  .object({
    modelNodeId: z.string().max(128).optional(),
    deviceId: z.string().max(64).optional(),
    pointKey: z.string().max(64).optional(),
    dataSetId: z.string().max(64).optional(),
    visualType: z.enum(VISUAL_TYPES).default('device-link'),
    ruleConfig: z.record(z.unknown()).default({}),
  })
  .strict();

export const sceneTreeNodeSchema: z.ZodType<SceneTreeNode, z.ZodTypeDef, unknown> = z.lazy(() =>
  z
    .object({
      id: z.string().min(1).max(128),
      name: z.string().min(1).max(200),
      collapsed: z.boolean().optional(),
      pickable: z.boolean().optional(),
      prefabRef: z
        .object({ prefabId: z.string().min(1).max(128), version: z.string().max(40).optional() })
        .strict()
        .optional(),
      transform: z
        .object({ position: vec3Schema.default([0, 0, 0]), rotation: vec3Schema.default([0, 0, 0]), scale: vec3Schema.default([1, 1, 1]) })
        .strict()
        .default({ position: [0, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1] }),
      visible: z.boolean().default(true),
      locked: z.boolean().default(false),
      layer: z.string().max(64).optional(),
      bindings: z.array(bindingSchema).max(20).optional(),
      children: z.array(sceneTreeNodeSchema).max(500).optional(),
    })
    .strict(),
);

export const sceneTreeSchema = z
  .object({
    schemaVersion: z.literal('2.0'),
    root: sceneTreeNodeSchema,
    cameras: z
      .array(
        z
          .object({ id: z.string().max(64), name: z.string().max(120), position: vec3Schema, target: vec3Schema })
          .strict(),
      )
      .max(50)
      .default([]),
    render: z.record(z.unknown()).default({}),
  })
  .strict();

/** 收集 scene_tree 中引用的全部 prefabId（去重） */
export function collectPrefabRefs(tree: SceneTree): string[] {
  const out = new Set<string>();
  const walk = (n: SceneTreeNode) => {
    if (n.prefabRef?.prefabId) out.add(n.prefabRef.prefabId);
    (n.children ?? []).forEach(walk);
  };
  walk(tree.root);
  return [...out];
}
